import type { ColumnDef } from "@tanstack/react-table";
import { IconChevronRight } from "@tabler/icons-react";

import { UserAvatar } from "@/components/common/UserAvatar";
import { StatusText } from "@/components/dashboard/dashboard-card";
import type { ObjectView } from "@/services/metaPerformance.service";
import { formatDate } from "@/utils/formatters";

import { BucketText, LaunchedMark, MixedMoney } from "./bits";
import { fmtInt, fmtMoney, fmtPct, fmtRoas } from "./format";

const num = (v: React.ReactNode) => <span className="tabular-nums text-foreground">{v}</span>;

/**
 * The one column set for campaigns, ad sets and ads. The name cell adapts to
 * the level (creator for ads, rule mark for campaigns); the figures are the
 * same everywhere so a drill-down reads as one table getting narrower.
 */
export function performanceColumns(level: "campaign" | "adset" | "ad", currency: string): ColumnDef<ObjectView>[] {
  const money = (v: number) => fmtMoney(v, currency);
  return [
    {
      id: "name",
      accessorKey: "name",
      header: level === "campaign" ? "Campaign" : level === "adset" ? "Ad set" : "Ad",
      meta: { width: "w-[26%]" },
      cell: ({ row }) => {
        const r = row.original;
        const creator = r.launched?.creator;
        return (
          <div className="flex min-w-0 items-center gap-2.5">
            {level === "ad" && r.launched?.content.thumbnailUrl ? (
              <img src={r.launched.content.thumbnailUrl} alt="" className="size-9 shrink-0 rounded-md object-cover" />
            ) : level === "ad" && creator ? (
              <UserAvatar src={creator.profileImage} name={`${creator.firstName} ${creator.lastName}`} className="size-9 shrink-0" />
            ) : null}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-foreground">{r.name}</span>
                <LaunchedMark on={r.reveshareLaunched} />
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {level === "ad" && creator
                  ? `${creator.firstName} ${creator.lastName}${creator.username ? ` · @${creator.username}` : ""}`
                  : level === "campaign" && r.rule
                    ? `Rule: ${[r.rule.roasFloor != null ? `ROAS ≥ ${r.rule.roasFloor}` : null, r.rule.roasCeiling != null ? `scale ≥ ${r.rule.roasCeiling}` : null, r.rule.ctrFloor != null ? `CTR ≥ ${r.rule.ctrFloor}%` : null].filter(Boolean).join(" · ")}${r.rule.enabled ? "" : " (off)"}`
                    : r.launched?.program.title ?? (r.objective ? r.objective.replace(/^OUTCOME_/, "").toLowerCase() : "")}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: "start",
      header: "Start",
      meta: { fit: true },
      cell: ({ row }) => {
        const t = row.original.startTime ?? row.original.createdTime;
        return <span className="whitespace-nowrap text-muted-foreground">{t ? formatDate(t) : "—"}</span>;
      },
    },
    {
      id: "status",
      header: "Status",
      meta: { fit: true },
      cell: ({ row }) => {
        const r = row.original;
        if (r.launched?.pausedByReveshareAt) return <StatusText status="PAUSED" label="Paused by rule" />;
        const s = r.effectiveStatus ?? r.status;
        return s ? <StatusText status={s === "ACTIVE" ? "ACTIVE" : s === "PAUSED" || s === "CAMPAIGN_PAUSED" || s === "ADSET_PAUSED" ? "PAUSED" : "INACTIVE"} label={s.toLowerCase().replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())} /> : <span className="text-muted-foreground">—</span>;
      },
    },
    { id: "spend", header: "Spend", meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" }, cell: ({ row }) => num(money(row.original.meta.spend)) },
    { id: "impressions", header: "Impr.", meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" }, cell: ({ row }) => num(fmtInt(row.original.meta.impressions)) },
    { id: "ctr", header: "CTR", meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" }, cell: ({ row }) => num(fmtPct(row.original.meta.ctr)) },
    { id: "cpc", header: "CPC", meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" }, cell: ({ row }) => num(row.original.meta.cpc != null ? money(row.original.meta.cpc) : "—") },
    { id: "purchases", header: "Purchases", meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" }, cell: ({ row }) => num(fmtInt(row.original.meta.purchases)) },
    { id: "revenue", header: "Revenue", meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" }, cell: ({ row }) => num(money(row.original.meta.purchaseValue)) },
    {
      id: "reveshare",
      header: "Reveshare",
      meta: { fit: true, cellClassName: "text-right", headerClassName: "text-right" },
      cell: ({ row }) => {
        const r = row.original.reveshare;
        if (r.orders === 0) return <span className="text-muted-foreground">—</span>;
        return (
          <span className="inline-flex flex-col items-end leading-tight">
            <span className="tabular-nums text-foreground"><MixedMoney m={r.revenue} fallbackCurrency={currency} /></span>
            <span className="text-[11px] text-muted-foreground">{r.orders} order{r.orders === 1 ? "" : "s"}</span>
          </span>
        );
      },
    },
    { id: "roas", header: "ROAS", meta: { fit: true }, cell: ({ row }) => <BucketText bucket={row.original.bucket} roas={fmtRoas(row.original.meta.roas)} /> },
    {
      id: "open",
      header: "",
      meta: { fit: true, cellClassName: "text-right", skeleton: <span /> },
      cell: () => <IconChevronRight size={16} stroke={1.75} className="inline-block text-muted-foreground" aria-hidden />,
    },
  ];
}
